import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { App, Button, Form, Modal, Select, Spin, theme } from "antd";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { ESubscriptionCancellationReason } from "@devowl-wp/api-real-commerce";
import { CancellationConfirmedModal } from "./cancellationConfirmedModal.js";
import { CancellationContractSummary } from "./cancellationContractSummary.js";
import { CancellationLossList } from "./cancellationLossList.js";
import { CancellationReasonContextualBanner } from "./cancellationReasonContextualBanner.js";
import { RealCommerceError } from "../../../../exceptions/realCommerceError.js";
import { trackAnalyticsEvent } from "../../../../others/tracking.js";
import { useStores } from "../../../../store/stores.js";
import { COLOR_ERROR_SOFT } from "../../../../style/antdTheme.js";
import { formatLocaleDate } from "../../../../utils/formatLocaleDate.js";
import { __, _i } from "../../../../utils/i18n.js";
import { useAccountV2ErrorHandling } from "../../hooks/useAccountV2ErrorHandling.js";
import { useAccountV2SubscriptionCancellationReason } from "../../hooks/useAccountV2SubscriptionCancellationReason.js";
const CancellationModal = observer(({ subscription })=>{
    const { token } = theme.useToken();
    const { modal: antdModal } = App.useApp();
    const { accountV2Store } = useStores();
    const handleRealCommerceError = useAccountV2ErrorHandling();
    const reasonCatalogue = useAccountV2SubscriptionCancellationReason();
    const [isBusy, setIsBusy] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [form] = Form.useForm();
    const effectiveEndDateLabel = formatLocaleDate(subscription.data.validUntil);
    const isRcb = String(subscription.data.productPlan.product.id) === "1";
    const reasonOptions = useMemo(()=>Object.values(reasonCatalogue).filter((entry)=>!!entry && !entry.isReadOnly).map((entry)=>({
                value: entry.value,
                label: entry.localized
            })), [
        reasonCatalogue
    ]);
    const selectedReason = Form.useWatch("cancellationReason", form);
    useEffect(()=>{
        trackAnalyticsEvent("cancellation_modal_opened", {
            ga: {
                event_category: "account",
                event_label: "Cancellation modal opened"
            },
            matomo: {
                category: "account",
                name: "Cancellation modal opened"
            }
        });
    }, []);
    useEffect(()=>{
        if (!selectedReason) {
            return;
        }
        trackAnalyticsEvent("cancellation_reason_selected", {
            ga: {
                event_category: "account",
                event_label: "Cancellation reason selected",
                value: selectedReason
            },
            matomo: {
                category: "account",
                name: "Cancellation reason selected",
                value: selectedReason
            }
        });
    }, [
        selectedReason
    ]);
    const close = ()=>accountV2Store.closeSubscriptionModal();
    const performCancellation = async (values)=>{
        setIsBusy(true);
        try {
            trackAnalyticsEvent("subscription_cancellation_confirmed", {
                ga: {
                    event_category: "account",
                    event_label: "Subscription cancellation confirmed"
                },
                matomo: {
                    category: "account",
                    name: "Subscription cancellation confirmed"
                }
            });
            await accountV2Store.cancelSubscription(subscription, {
                cancellationReason: values.cancellationReason ?? ESubscriptionCancellationReason.Unknown
            });
            setIsSuccess(true);
        } catch (e) {
            if (e instanceof RealCommerceError) {
                handleRealCommerceError(e);
            }
        } finally{
            setIsBusy(false);
        }
    };
    const submit = async ()=>{
        let values;
        try {
            values = await form.validateFields();
        } catch  {
            return;
        }
        antdModal.confirm({
            title: __("Confirm cancellation"),
            icon: /*#__PURE__*/ _jsx(ExclamationCircleFilled, {
                style: {
                    color: COLOR_ERROR_SOFT
                }
            }),
            content: /*#__PURE__*/ _jsx("div", {
                className: "rc-account-v2-cancellation-modal__confirm-body",
                children: _i(__("{{strong}}This cancellation is irreversible.{{/strong}} Once confirmed, we can't undo it for you."), {
                    strong: /*#__PURE__*/ _jsx("strong", {
                        className: "rc-cancel-loss-lead rc-account-v2-cancellation-modal__confirm-strong"
                    })
                })
            }),
            okText: __("Yes, cancel now"),
            okButtonProps: {
                danger: true,
                type: "primary"
            },
            cancelText: __("Keep my subscription"),
            onOk: ()=>performCancellation(values)
        });
    };
    if (isSuccess) {
        return /*#__PURE__*/ _jsx(CancellationConfirmedModal, {
            effectiveEndDateLabel: effectiveEndDateLabel,
            onClose: close
        });
    }
    return /*#__PURE__*/ _jsx(Modal, {
        open: true,
        title: /*#__PURE__*/ _jsx("div", {
            className: "rc-account-v2-cancellation-modal__title",
            style: {
                color: token.colorTextHeading
            },
            children: __("Cancel subscription")
        }),
        onCancel: close,
        footer: null,
        destroyOnClose: true,
        maskClosable: !isBusy,
        width: 640,
        children: /*#__PURE__*/ _jsxs(Spin, {
            spinning: isBusy || subscription.isBusy,
            children: [
                /*#__PURE__*/ _jsx(CancellationContractSummary, {
                    subscription: subscription
                }),
                /*#__PURE__*/ _jsxs(Form, {
                    form: form,
                    layout: "vertical",
                    children: [
                        /*#__PURE__*/ _jsx(Form.Item, {
                            name: "cancellationReason",
                            label: /*#__PURE__*/ _jsx("strong", {
                                children: __("Why are you cancelling?")
                            }),
                            rules: [
                                {
                                    required: true,
                                    message: __("Please select a reason.")
                                }
                            ],
                            style: {
                                marginBottom: 12
                            },
                            children: /*#__PURE__*/ _jsx(Select, {
                                placeholder: __("Select a reason"),
                                options: reasonOptions
                            })
                        }),
                        /*#__PURE__*/ _jsx(CancellationReasonContextualBanner, {
                            selectedReason: selectedReason,
                            isRcb: isRcb
                        }),
                        selectedReason === ESubscriptionCancellationReason.Unknown && /*#__PURE__*/ _jsx("div", {
                            className: "rc-account-v2-cancellation-modal__skip-reason-hint",
                            style: {
                                color: token.colorTextSecondary
                            },
                            children: __("Without your feedback, we won't know what we could have done differently...")
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "rc-account-v2-cancellation-modal__end-date",
                            style: {
                                color: token.colorText
                            },
                            children: __("If you cancel now, you'll keep full access until %s. Your subscription ends then; nothing changes before that.", effectiveEndDateLabel)
                        }),
                        /*#__PURE__*/ _jsx(CancellationLossList, {
                            isRcb: isRcb
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "rc-account-v2-cancellation-modal__footer",
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            onClick: close,
                            disabled: isBusy,
                            children: __("Keep my subscription")
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            danger: true,
                            type: "primary",
                            onClick: submit,
                            loading: isBusy,
                            disabled: isBusy,
                            children: __("Cancel now")
                        })
                    ]
                })
            ]
        })
    });
});
export { CancellationModal };
